// The derived ingestion statuses, the approval gate and the edit blocks: the
// browser's twin of the backend's functions/ingestion/cleaning/statuses.py and
// of the upload boundary's member_state in functions/ingestion/persistence.py.
// Keep them in step by hand: this is the only place the frontend derives a
// status (Spec 2 section 5).
//
// A status is never chosen by whoever writes it. It is derived from the
// document's own fields by the functions here and stored by whoever changes
// one of those fields, in the same transaction (services/cleaningGestures.js).

import { activeDecisions, isPending } from './decisions';
import { isRawTable, isTimeSeriesTable } from './tableTypes';

// The unit statuses that keep a file in review.
export const REVIEW_STATUSES = new Set(['PreviewReady', 'MissingRowIdentifier', 'ZeroColumnsMapped', 'Failed']);

// The edit-block sentences (Spec 2 section 6.2), shown as the reason a control
// is disabled and as the toast when a gesture is refused.
export const INTAKE_EDIT_BLOCK = 'This file could not be read. Delete it and upload it again.';
export const CLEANING_EDIT_BLOCK = 'This file could not be processed. Delete it and upload it again.';
export const DELETING_EDIT_BLOCK = 'This file is being deleted.';
export const COMPLETED_EDIT_BLOCK = 'This file is complete. To change it, delete it and upload it again.';
export const BUSY_FILE_EDIT_BLOCK = 'Decode is still working on this file; nothing can be changed yet.';
export const RUNNING_BATCH_EDIT_BLOCK = 'This file is being checked against the table; nothing can be changed until that finishes.';
export const SUBMITTING_EDIT_BLOCK = 'Answers are being submitted; wait until that finishes.';
export const BUSY_UNIT_EDIT_BLOCK = 'Decode is updating this table.';
export const FAILED_UNIT_EDIT_BLOCK = 'This table could not be processed. Adjust its bounds to try again, or ignore it.';
export const APPROVED_UNIT_EDIT_BLOCK = 'This table is approved. Un-approve it to make changes.';

// What the files list and the file card call each file status (the mockup's
// FILE_LABEL in whiteboard/cleaning_mockups/ui.js).
export const FILE_LABEL = {
  Cleaning: 'Cleaning',
  Reviewing: 'To review',
  AwaitingApproval: 'Ready to approve',
  Ready: 'Queued',
  running: 'Being checked',
  parked: 'Needs answers',
  waiting: 'Queued',
  completed: 'Completed',
  Failed: 'Problem',
  AwaitingDeletion: 'Deleting',
  Deleting: 'Deleting',
  DeleteFailed: 'Could not delete',
};

// What a unit card calls each unit status (the mockup's UNIT_LABEL, less
// ConflictsDetected: a batch's questions are the file's, and no unit status
// says "has questions"). A unit used for header values is Ignored by status
// too; the mockup's card names it by its disposition instead, 'Header values'.
export const UNIT_LABEL = {
  PreviewReady: 'To review',
  MissingRowIdentifier: 'Needs the identifier',
  ZeroColumnsMapped: 'Nothing mapped',
  AwaitingValidation: 'Being checked',
  Ready: 'Approved',
  Completed: 'Completed',
  Cleaning: 'Cleaning',
  Failed: 'Problem',
  Ignored: 'Ignored',
};

const PERIOD_MAPPING_KEY = 'period';
const MAP_PROCESS = 'map';

// A rule whose action changes cell values runs as one Rule process; a rule
// that removes rows as one Skip process (functions/ingestion/cleaning/rule_defs.py).
const MUTATING_ACTIONS = ['trim', 'remove_spaces', 'set_value', 'concatenate', 'transform_value'];
const SKIP_ACTION = 'skip_row';

function hasFailVerdict(verdicts, processes) {
  return verdicts.some((verdict) => verdict.level === 'fail' && processes.includes(verdict.process));
}

// The ids of the processes in a unit's chain for its table, in chain order
// (functions/ingestion/cleaning/chains.py::unit_processes): Map, Types, one
// Rule per enabled mutating rule in saved order, one Classify per classified
// column, one Skip per enabled skip rule in saved order, then, on a
// time-series table only, Placeholders and UnitDate.
export function unitChainProcessIds(table) {
  // The table's ingestion rules as a list in saved order (rule_defs.
  // normalize_rules_to_list). They are stored as a list, or as a map keyed by
  // position.
  const rules = table.ingestion_rules;
  let ordered = [];
  if (Array.isArray(rules)) {
    ordered = rules.filter((rule) => rule != null);
  } else if (rules && typeof rules === 'object') {
    let positions = Object.keys(rules);
    if (positions.every((position) => /^\s*[-+]?\d+\s*$/.test(position))) {
      positions = [...positions].sort((first, second) => Number(first) - Number(second));
    }
    ordered = positions.map((position) => rules[position]).filter((rule) => rule != null);
  } else if (rules) {
    throw new Error(`Ingestion rules must be a list or a map, not ${typeof rules}.`);
  }
  // A rule with no `enabled` field is enabled, as rule.get('enabled', True)
  // reads it. A unit's records name a rule's process by its id, so an enabled
  // rule without one is refused, as the backend refuses it.
  const enabledRules = ordered.filter((rule) => (rule.enabled === undefined ? true : Boolean(rule.enabled)));
  for (const rule of enabledRules) {
    if (!rule.id) {
      throw new Error(`The ingestion rule '${rule.name || 'Unnamed rule'}' has no id. `
        + 'Delete it in Table Settings and add it again.');
    }
  }

  const chain = [MAP_PROCESS, 'types'];
  for (const rule of enabledRules) {
    if (MUTATING_ACTIONS.includes((rule.action || {}).type)) chain.push(`rule:${rule.id}`);
  }
  // The table's settings are browser-written and admitted whole: a
  // classification that is not a map names no column, so it gives no Classify
  // process, as the backend's chain gives none.
  const classification = table.classification;
  if (classification !== null && typeof classification === 'object' && !Array.isArray(classification)) {
    for (const column of Object.keys(classification)) chain.push(`classify:${column}`);
  }
  for (const rule of enabledRules) {
    if ((rule.action || {}).type === SKIP_ACTION) chain.push(`skip:${rule.id}`);
  }
  if (isTimeSeriesTable(table)) {
    chain.push('placeholders');
    chain.push('unit_date');
  }
  return chain;
}

// A request, or an edit newer than its run, on one of the processes in the
// unit's chain (statuses.unit_has_waiting_work). A record of a process no
// longer in the chain, a rule or a classified column removed from the table,
// is not waiting work: the unit's next run drops it (00 A.29).
function unitHasWaitingWork(unit, table) {
  const chain = unitChainProcessIds(table);
  return Object.entries(unit.processes).some(([processId, record]) => (
    chain.includes(processId) && isPending(record)
  ));
}

// Destination column -> its active Map value, for every target the unit's Map
// record holds. A target is unmapped when the Map record has no active value
// for it (get answers undefined), or its active value is None (Not Present).
function activeMapping(unit) {
  const mapping = new Map();
  const mapRecord = unit.processes[MAP_PROCESS];
  if (mapRecord === undefined) return mapping;
  for (const { key, version } of activeDecisions(mapRecord)) mapping.set(key, version.value);
  return mapping;
}

export function statusFrom(unit, file, table) {
  if (unit.disposition !== 'ingest') return 'Ignored';
  // The reaper's verdict is terminal: an edit waiting behind it cannot restart the unit.
  if (hasFailVerdict(unit.verdicts, ['cleaning'])) return 'Failed';
  if (unit.claim) return 'Cleaning';
  if (unitHasWaitingWork(unit, table)) return 'Cleaning';
  // Judged before the no-records test: the reaper can give up before a first commit.
  if ([...unit.verdicts, ...unit.base_verdicts].some((verdict) => verdict.level === 'fail')) return 'Failed';
  if (Object.keys(unit.processes).length === 0) return 'Cleaning';
  const mapping = activeMapping(unit);
  if (isRawTable(table) && ![...mapping.values()].some((value) => value != null)) return 'ZeroColumnsMapped';
  if (isTimeSeriesTable(table) && mapping.get(table.identifier.name) == null) return 'MissingRowIdentifier';
  if (!unit.approved) return 'PreviewReady';
  if (file.batch_id == null) return 'Ready';
  if (file.batch_status === 'completed') return 'Completed';
  return 'AwaitingValidation';
}

export function fileStatusFrom(file) {
  const deletion = file.deletion;
  if (deletion) {
    if (deletion.failed) return 'DeleteFailed';
    return deletion.claimed_at ? 'Deleting' : 'AwaitingDeletion';
  }
  // Delete-only verdicts, before the claim and no-records tests.
  if (hasFailVerdict(file.verdicts, ['intake', 'cleaning'])) return 'Failed';
  if (file.claim || Object.keys(file.processes).length === 0) return 'Cleaning';
  if (Object.values(file.processes).some((record) => isPending(record))) return 'Cleaning';
  const entries = Object.values(file.units);
  if (entries.some((entry) => entry.status === 'Cleaning')) return 'Cleaning';
  // The walk's failures, until Submit; the file chain's own (no tables) too.
  if (file.verdicts.some((verdict) => verdict.level === 'fail')) return 'Failed';
  if (entries.some((entry) => REVIEW_STATUSES.has(entry.status))) return 'Reviewing';
  if (!file.approved) return 'AwaitingApproval';
  if (file.batch_id == null) return 'Ready';
  return file.batch_status;
}

// Any ledger entry with warnings, or a warn verdict on the file itself: the
// file's has_warnings, which the files list queries.
export function fileHasWarnings(file) {
  if (Object.values(file.units).some((entry) => entry.warnings > 0)) return true;
  return file.verdicts.some((verdict) => verdict.level === 'warn');
}

// The files list's section for a file status: 'deleting', 'processing',
// 'attention' or 'completed'. A file whose deletion failed needs attention:
// its owner has to try again (00 A.82, Spec 6 section 5).
export function bucketOf(status) {
  if (['AwaitingDeletion', 'Deleting'].includes(status)) return 'deleting';
  if (['Cleaning', 'Ready', 'running', 'waiting'].includes(status)) return 'processing';
  if (status === 'completed') return 'completed';
  return 'attention';
}

// The mapping targets every unit must map: on a time-series table the detail
// columns and metric leaves required per unit; on a raw table every data
// column required in either scope, since raw rows are independent. Attribute
// columns are excluded: upload metadata supplies them.
function unitRequiredTargets(table) {
  const required = [];
  for (const column of table.detailColumns || []) {
    if (column.mandatory_per_unit || (isRawTable(table) && column.mandatory_per_file)) {
      required.push(column.name);
    }
  }
  if (isTimeSeriesTable(table)) {
    // Breadth first through the metric groups, as the backend walks them, so
    // the blockers come in the same order.
    const pending = [{ schema: (table.metricColumns || {}).schema || {}, prefix: '' }];
    while (pending.length > 0) {
      const { schema, prefix } = pending.shift();
      for (const [name, definition] of Object.entries(schema)) {
        if (definition === null || typeof definition !== 'object' || Array.isArray(definition)) continue;
        const path = prefix ? `${prefix}.${name}` : name;
        if (!('type' in definition)) {
          pending.push({ schema: definition, prefix: path });
        } else if (definition.mandatory_per_unit) {
          required.push(`${PERIOD_MAPPING_KEY}.${path}`);
        }
      }
    }
  }
  return required;
}

// The plain-language list under "Before this can be approved"; empty when the
// unit may be approved. Reads the unit and the table schema only, never rows,
// siblings or the file (Spec 2 section 5.2).
export function gateBlockers(unit, table) {
  const mapping = activeMapping(unit);
  const blockers = [];
  if (isTimeSeriesTable(table)) {
    const identifier = table.identifier.name;
    if (mapping.get(identifier) == null) blockers.push(`${identifier} is not mapped`);
  }
  const periodPrefix = `${PERIOD_MAPPING_KEY}.`;
  for (const target of unitRequiredTargets(table)) {
    if (mapping.get(target) == null) {
      const name = target.startsWith(periodPrefix) ? target.slice(periodPrefix.length) : target;
      blockers.push(`${name} is required but not mapped`);
    }
  }
  if (isRawTable(table) && ![...mapping.values()].some((value) => value != null)) {
    blockers.push('At least one column must be mapped');
  }
  if (isTimeSeriesTable(table)) {
    const metricsMapped = [...mapping.entries()].some(([target, value]) => (
      target.startsWith(periodPrefix) && value != null
    ));
    const periodMapped = mapping.get(PERIOD_MAPPING_KEY) != null;
    if (metricsMapped && !periodMapped) blockers.push('Reporting period must be mapped when metrics are mapped');
    if (periodMapped && !metricsMapped) blockers.push('Unmap reporting period when no metrics are mapped');
  }
  for (const verdict of [...unit.verdicts, ...unit.base_verdicts]) {
    if (verdict.level === 'fail') blockers.push(verdict.message);
  }
  return blockers;
}

// Whether the file has runnable work pending or claimed: its own chain, and
// with includeUnits, any unit whose entry is Cleaning or pending. A retained
// request on a terminally failed or non-ingest unit is not runnable and does
// not count.
export function fileIsBusy(file, { includeUnits }) {
  if (file.claim != null || file.cleanup_attempts > 0) return true;
  const refused = hasFailVerdict(file.verdicts, ['intake', 'cleaning']);
  const neverCleaned = Object.keys(file.processes).length === 0;
  if (!refused && (neverCleaned || Object.values(file.processes).some((record) => isPending(record)))) {
    return true;
  }
  if (!includeUnits) return false;
  for (const entry of Object.values(file.units)) {
    if (entry.status === 'Cleaning') return true;
    if (entry.pending && entry.disposition === 'ingest' && entry.status !== 'Failed') return true;
  }
  return false;
}

// Why the file's batch stops any change to it, or ''. The batch document is
// read in the gesture's transaction: a parked file's mirrored status does not
// show a submission in progress.
export function batchEditBlock(file, batch) {
  if (file.batch_status === 'running' || (batch != null && batch.status === 'running')) {
    return RUNNING_BATCH_EDIT_BLOCK;
  }
  if (batch != null && batch.submitting != null) return SUBMITTING_EDIT_BLOCK;
  return '';
}

// The four cases in which nothing on a file can be changed whatever is
// running, the first checks of both edit blocks: one being deleted or whose
// deletion failed (its sentence, with Try again), a refused file, one the
// reaper gave up on, and a completed one. The deletion comes first, as the
// edit block covers every file whose deletion is set (Spec 5 section 5): a
// refused file is the one the person is told to delete, and once they have,
// the deletion is what it waits on.
function closedFileEditBlock(file) {
  if (file.deletion != null) return file.deletion.failed || DELETING_EDIT_BLOCK;
  if (hasFailVerdict(file.verdicts, ['intake'])) return INTAKE_EDIT_BLOCK;
  if (hasFailVerdict(file.verdicts, ['cleaning'])) return CLEANING_EDIT_BLOCK;
  if (file.batch_status === 'completed') return COMPLETED_EDIT_BLOCK;
  return '';
}

// Why the file's own decisions cannot be edited now, or '' when they can.
export function fileEditBlock(file, batch) {
  const closed = closedFileEditBlock(file);
  if (closed) return closed;
  // A file decision reruns the file chain, which rewrites every unit's copies.
  if (fileIsBusy(file, { includeUnits: true })) return BUSY_FILE_EDIT_BLOCK;
  return batchEditBlock(file, batch);
}

// The file's part of a unit's edit block: why no unit of this file can be
// edited, approved or un-approved now, or ''. The approval gestures judge a
// unit from its ledger entry and this, without reading the unit (Spec 2
// section 5.3).
export function unitFileEditBlock(file, batch) {
  const closed = closedFileEditBlock(file);
  if (closed) return closed;
  const batchBlock = batchEditBlock(file, batch);
  if (batchBlock) return batchBlock;
  // The file may be about to rewrite its units' copies.
  if (fileIsBusy(file, { includeUnits: false })) return BUSY_FILE_EDIT_BLOCK;
  return '';
}

// Why one unit's decisions cannot be edited now, or '' when they can. A
// sibling being processed does not block this unit (00 A.27). The table names
// the unit's chain, whose records alone are waiting work.
export function unitEditBlock(unitId, unit, file, batch, table) {
  const fileBlock = unitFileEditBlock(file, batch);
  if (fileBlock) return fileBlock;
  if (unit.claim != null || unit.cleanup_attempts > 0) return BUSY_UNIT_EDIT_BLOCK;
  const runnable = unit.disposition === 'ingest' && !hasFailVerdict(unit.verdicts, ['cleaning']);
  const entry = file.units[unitId];
  // A unit the file's ledger does not list (its snapshot arrived before the
  // file's, or the file chain is removing it) is the file chain's work in progress.
  if (entry === undefined) return BUSY_UNIT_EDIT_BLOCK;
  const entryBusy = entry.pending || entry.status === 'Cleaning';
  const neverRun = Object.keys(unit.processes).length === 0;
  if (runnable && (entryBusy || neverRun || unitHasWaitingWork(unit, table))) return BUSY_UNIT_EDIT_BLOCK;
  if (hasFailVerdict(unit.verdicts, ['cleaning'])) return FAILED_UNIT_EDIT_BLOCK;
  if (unit.approved) return APPROVED_UNIT_EDIT_BLOCK;
  return '';
}

// The upload-manifest state of a file member: 'ok', 'error' or 'pending'.
// Read from the file document alone. A requested or retried deletion holds the
// upload open; a failed deletion is an error. A landed file is ok. A refusal
// (an intake verdict) and a file the reaper gave up on (a cleaning verdict) are
// delete-only, so they close the member as an error; every other fail verdict
// is fixed before the file can land and keeps the member pending.
export function memberState(file) {
  if (file.deletion) return file.deletion.failed ? 'error' : 'pending';
  if (file.batch_status === 'completed') return 'ok';
  const refused = file.verdicts.some((verdict) => (
    verdict.level === 'fail' && ['intake', 'cleaning'].includes(verdict.process)
  ));
  return refused ? 'error' : 'pending';
}
